import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, map, catchError } from 'rxjs';

export interface EmagIssue {
  id: number;
  title: string;
  description?: string | null;
  category?: string | null;
  publish_date?: string | null;
  cover_path?: string | null;
  pdf_path?: string | null;
}

@Injectable({ providedIn: 'root' })
export class EmagazineService {
  // Use the working backend you showed
  private base = 'https://fmcet.ac.in/backend';

  constructor(private http: HttpClient) {}

  /** GET all published issues */
  listPublic(): Observable<EmagIssue[]> {
    return this.http
      .get<any>(`${this.base}/getEmagazine.php`, { responseType: 'json' as const })
      .pipe(
        map(raw => this.normalizeList(raw)),
        catchError(err => {
          console.error('listPublic failed', err);
          return of([] as EmagIssue[]);
        })
      );
  }

  /** GET single issue by id */
  getById(id: number): Observable<EmagIssue> {
    return this.http
      .get<any>(`${this.base}/getEmagazineById.php?id=${encodeURIComponent(id)}`, { responseType: 'json' as const })
      .pipe(
        map(raw => {
          // backend might return an array or a single object
          const list = this.normalizeList(raw);
          return list[0] ?? this.normalizeIssue(raw);
        }),
        catchError(err => {
          console.error('getById failed', err);
          // return a minimal placeholder so the reader page still renders
          return of({
            id,
            title: 'Unavailable',
            description: '',
            category: null,
            publish_date: null,
            cover_path: null,
            pdf_path: null
          } as EmagIssue);
        })
      );
  }

  // ---------- helpers ----------

  /** Accepts: array, {data: array}, or JSON string of either. Returns a normalized array. */
  private normalizeList(raw: any): EmagIssue[] {
    try {
      if (typeof raw === 'string') raw = JSON.parse(raw);
    } catch {
      return [];
    }
    const arr = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
    return arr.map(x => this.normalizeIssue(x));
  }

  /** Coerces types and fixes relative URLs to absolute. */
  private normalizeIssue(x: any): EmagIssue {
    const id = Number(x?.id ?? x?.issue_id ?? 0);

    const strOrNull = (v: any) =>
      v === undefined || v === null || v === '' ? null : String(v);

    const absUrl = (u: any): string | null => {
      const s = strOrNull(u);
      if (!s) return null;
      if (/^https?:\/\//i.test(s)) return s;
      return `https://fmcet.ac.in${s.startsWith('/') ? '' : '/'}${s}`;
    };

    return {
      id,
      title: String(x?.title ?? ''),
      description: strOrNull(x?.description),
      category: strOrNull(x?.category),
      publish_date: strOrNull(x?.publish_date),
      cover_path: absUrl(x?.cover_path),
      pdf_path: absUrl(x?.pdf_path)
    };
  }
}
